import type {
  CatCompanionAddonConfig,
  CatCompanionVariantConfig,
  CatToyName,
} from "../vts-config";
import {
  defaultPetBottomRange,
  getPetBounds,
  getPetBottomRange,
  getPetDepth,
  getPetLayer,
  showPetAlert,
} from "./pet-layer";

type CatAtlas = {
  frameSize: number;
  size: SheetSize;
  animations: Record<string, CatAnimation>;
  toys: {
    image: string;
    size: SheetSize;
    sprites: Record<string, SpriteClip>;
  };
};

type CatAnimation = {
  row: number;
  frames: number;
  frameMs: number;
};

type SheetSize = {
  w: number;
  h: number;
};

type SpriteClip = {
  x: number;
  y: number;
  w: number;
  h: number;
  frames: number;
  frameMs: number;
};

type Facing = -1 | 1;

const defaultConfig: Required<
  Pick<
    CatCompanionAddonConfig,
    | "atlas"
    | "scale"
    | "horizontalPaddingPx"
    | "bottomRangePx"
    | "runSpeedPxPerSecond"
    | "spawnDelayMs"
    | "minDistancePx"
    | "toyChance"
    | "toys"
  >
> = {
  atlas: "/addons/cats/cat-sprite-sheet.json",
  scale: 3,
  horizontalPaddingPx: 96,
  bottomRangePx: defaultPetBottomRange,
  runSpeedPxPerSecond: 170,
  spawnDelayMs: [12_000, 28_000],
  minDistancePx: 220,
  toyChance: 0.2,
  toys: ["ball", "mouse", "wand", "fish"],
};

const defaultVariants: CatCompanionVariantConfig[] = [
  {
    id: "temtem",
    label: "TemTem",
    sprite: "/addons/cats/temtem.png",
  },
  {
    id: "tama",
    label: "Tama",
    sprite: "/addons/cats/tama.png",
  },
];

const ballSprites = ["ball-blue", "ball-orange", "ball-pink"];
// The mouse chase is long, so it comes up less often than the other toys.
const toyWeights: Record<CatToyName, number> = {
  ball: 3,
  wand: 3,
  fish: 3,
  mouse: 1,
};
const restActivities: Array<[string, number]> = [
  ["idle", 3],
  ["groom", 2],
  ["sit", 3],
  ["nap", 4],
  ["wander", 1.5],
  ["jump", 0.5],
];
// Transparent rows below the paws in the cat frames.
const catFootGapPx = 2;
// How quickly a kicked ball slows down, in px/s^2.
const ballFriction = 160;
// How close a mouse sneaks up before the cat notices it.
const mouseNoticeDistancePx = 190;
// Radius of one bite, in sprite pixels.
const biteRadiusPx = 2.3;

let activeAddon: CatCompanionAddon | null = null;

export function initializeCatCompanions(
  config: CatCompanionAddonConfig | undefined,
): void {
  if (!config?.enabled || activeAddon) {
    return;
  }

  activeAddon = new CatCompanionAddon(config);
  void activeAddon.start();
}

class CatCompanionAddon {
  public readonly scale: number;
  public readonly runSpeed: number;
  private readonly config: CatCompanionAddonConfig;
  private readonly layer = getPetLayer();
  private readonly sprites = new Set<SheetSprite>();
  private readonly cats: CatCompanion[] = [];
  private atlas: CatAtlas | null = null;
  private toyImage = "";
  private toyPixels: HTMLImageElement | null = null;
  private lastFrameTime = 0;

  public constructor(config: CatCompanionAddonConfig) {
    this.config = config;
    this.scale = config.scale ?? defaultConfig.scale;
    this.runSpeed =
      config.runSpeedPxPerSecond ?? defaultConfig.runSpeedPxPerSecond;
  }

  public async start(): Promise<void> {
    const atlasUrl = this.config.atlas ?? defaultConfig.atlas;
    this.atlas = await fetchJson<CatAtlas>(atlasUrl);
    this.toyImage = new URL(
      this.atlas.toys.image,
      new URL(atlasUrl, window.location.href),
    ).href;

    const variants = this.config.variants?.length
      ? this.config.variants
      : defaultVariants;

    let spawnDelay = 0;
    variants.forEach((variant) => {
      const cat = new CatCompanion(variant, this);
      this.cats.push(cat);
      void cat.live(spawnDelay);
      spawnDelay += randomBetween(...this.getSpawnDelayMs());
    });

    window.requestAnimationFrame((time) => this.tick(time));
  }

  public createCatSprite(variant: CatCompanionVariantConfig): SheetSprite {
    const sprite = this.createSprite(variant.sprite, this.getAtlas().size);
    sprite.element.classList.add("cat-companion");
    sprite.element.dataset.cat = variant.id;
    return sprite;
  }

  public createToySprite(
    name: string,
    owner: SheetSprite,
    frame?: number,
  ): SheetSprite {
    const atlas = this.getAtlas();
    const clip = atlas.toys.sprites[name];
    const sprite = this.createSprite(this.toyImage, atlas.toys.size);
    sprite.depthBottom = owner.bottom;
    sprite.element.classList.add("cat-companion-toy");
    sprite.element.dataset.toy = name;
    sprite.play(
      frame === undefined
        ? clip
        : { ...clip, x: clip.x + frame * clip.w, frames: 1 },
    );
    return sprite;
  }

  // Eats a toy away in random bites instead of fading it out.
  public async eatToy(toy: SheetSprite, duration: number): Promise<void> {
    const clip = toy.getClip();
    const image = await this.loadToyPixels();
    if (!clip || !image) {
      toy.setVisible(false);
      await wait(duration);
      return;
    }

    const reader = document.createElement("canvas");
    reader.width = clip.w;
    reader.height = clip.h;
    const readerContext = reader.getContext("2d");
    const mask = document.createElement("canvas");
    mask.width = clip.w * this.scale;
    mask.height = clip.h * this.scale;
    const maskContext = mask.getContext("2d");
    if (!readerContext || !maskContext) {
      toy.setVisible(false);
      await wait(duration);
      return;
    }

    readerContext.drawImage(
      image,
      clip.x,
      clip.y,
      clip.w,
      clip.h,
      0,
      0,
      clip.w,
      clip.h,
    );
    const alpha = readerContext.getImageData(0, 0, clip.w, clip.h).data;
    let remaining: Array<[number, number]> = [];
    for (let y = 0; y < clip.h; y += 1) {
      for (let x = 0; x < clip.w; x += 1) {
        if (alpha[(y * clip.w + x) * 4 + 3] > 0) {
          remaining.push([x, y]);
        }
      }
    }

    const bites: Array<Array<[number, number]>> = [];
    while (remaining.length > 0) {
      const [biteX, biteY] = pickRandom(remaining);
      const isBitten = ([x, y]: [number, number]): boolean =>
        Math.hypot(x - biteX, y - biteY) <= biteRadiusPx;

      bites.push(remaining.filter(isBitten));
      remaining = remaining.filter((pixel) => !isBitten(pixel));
    }

    maskContext.fillStyle = "#000";
    maskContext.fillRect(0, 0, mask.width, mask.height);
    for (const bite of bites) {
      await wait(duration / bites.length);
      bite.forEach(([x, y]) => {
        maskContext.clearRect(
          x * this.scale,
          y * this.scale,
          this.scale,
          this.scale,
        );
      });
      toy.setMask(mask.toDataURL());
    }
  }

  public removeSprite(sprite: SheetSprite): void {
    this.sprites.delete(sprite);
    sprite.element.remove();
  }

  public getCatClip(name: string): SpriteClip {
    const atlas = this.getAtlas();
    const animation = atlas.animations[name];

    return {
      x: 0,
      y: animation.row * atlas.frameSize,
      w: atlas.frameSize,
      h: atlas.frameSize,
      frames: animation.frames,
      frameMs: animation.frameMs,
    };
  }

  public getToyClip(name: string): SpriteClip {
    return this.getAtlas().toys.sprites[name];
  }

  public getToys(): CatToyName[] {
    return this.config.toys ?? defaultConfig.toys;
  }

  public getToyChance(): number {
    return this.config.toyChance ?? defaultConfig.toyChance;
  }

  public getBounds(): [number, number] {
    const padding =
      this.config.horizontalPaddingPx ?? defaultConfig.horizontalPaddingPx;
    return getPetBounds(this.getAtlas().frameSize * this.scale, padding);
  }

  public getMinDistance(): number {
    return this.config.minDistancePx ?? defaultConfig.minDistancePx;
  }

  public measureGap(cat: CatCompanion, x: number): number {
    return this.cats.reduce((gap, other) => {
      return other === cat || !other.isPresent()
        ? gap
        : Math.min(gap, Math.abs(other.getRestingX() - x));
    }, Number.POSITIVE_INFINITY);
  }

  public pickSpot(cat: CatCompanion): number {
    const [minX, maxX] = this.getBounds();
    let bestSpot = randomBetween(minX, maxX);
    let bestGap = this.measureGap(cat, bestSpot);

    for (let attempt = 0; attempt < 12; attempt += 1) {
      if (bestGap >= this.getMinDistance() * 1.5) {
        break;
      }

      const spot = randomBetween(minX, maxX);
      const gap = this.measureGap(cat, spot);
      if (gap > bestGap) {
        bestSpot = spot;
        bestGap = gap;
      }
    }

    return bestSpot;
  }

  public pickBottom(): number {
    const [minBottom, maxBottom] = getPetBottomRange(
      this.config.bottomRangePx ?? defaultConfig.bottomRangePx,
      this.getAtlas().frameSize * this.scale,
    );

    return randomBetween(minBottom, maxBottom);
  }

  private getSpawnDelayMs(): [number, number] {
    return this.config.spawnDelayMs ?? defaultConfig.spawnDelayMs;
  }

  private loadToyPixels(): Promise<HTMLImageElement | null> {
    if (this.toyPixels) {
      return Promise.resolve(this.toyPixels);
    }

    return new Promise((resolve) => {
      const image = new Image();
      image.onload = () => {
        this.toyPixels = image;
        resolve(image);
      };
      image.onerror = () => resolve(null);
      image.src = this.toyImage;
    });
  }

  private createSprite(image: string, sheetSize: SheetSize): SheetSprite {
    const sprite = new SheetSprite(image, sheetSize, this.scale);
    this.sprites.add(sprite);
    this.layer.append(sprite.element);
    return sprite;
  }

  private getAtlas(): CatAtlas {
    if (!this.atlas) {
      throw new Error("Cat companion atlas is not loaded");
    }

    return this.atlas;
  }

  private tick(time: number): void {
    // Clamp the step so a backgrounded tab does not fast-forward on return.
    const elapsed = Math.min(100, time - (this.lastFrameTime || time));
    this.lastFrameTime = time;
    this.sprites.forEach((sprite) => sprite.update(elapsed));

    window.requestAnimationFrame((nextTime) => this.tick(nextTime));
  }
}

class SheetSprite {
  public readonly element = document.createElement("div");
  public x = 0;
  public bottom = 0;
  // Toys sort with their owner, even when they sit a little higher or lower.
  public depthBottom: number | null = null;
  // Extra height above the ground line, for small hops.
  public lift = 0;
  public facing: Facing = 1;
  private clip: SpriteClip | null = null;
  private loop = true;
  private frameIndex = 0;
  private frameElapsed = 0;
  private finishPlay: (() => void) | null = null;
  private target: { x: number; speed: number } | null = null;
  private slide: {
    from: number;
    to: number;
    duration: number;
    elapsed: number;
  } | null = null;
  private finishMove: (() => void) | null = null;

  public constructor(
    image: string,
    private readonly sheetSize: SheetSize,
    private readonly scale: number,
  ) {
    this.element.className = "cat-companion-sprite";
    this.element.style.backgroundImage = `url("${image}")`;
    this.element.style.backgroundSize = `${sheetSize.w * scale}px ${
      sheetSize.h * scale
    }px`;
  }

  public play(clip: SpriteClip): void {
    this.startClip(clip, true);
  }

  public playOnce(clip: SpriteClip): Promise<void> {
    this.startClip(clip, false);
    return new Promise((resolve) => {
      this.finishPlay = resolve;
    });
  }

  public moveTo(x: number, speed: number): Promise<void> {
    this.finishMove?.();
    this.slide = null;
    if (x !== this.x) {
      this.facing = x > this.x ? 1 : -1;
    }
    this.target = { x, speed };

    return new Promise((resolve) => {
      this.finishMove = resolve;
    });
  }

  // Slides to x under constant friction (px/s^2): a longer slide starts
  // faster, and every slide slows down at the same rate.
  public slideTo(x: number, friction: number): Promise<void> {
    this.finishMove?.();
    this.target = null;
    if (x !== this.x) {
      this.facing = x > this.x ? 1 : -1;
    }
    this.slide = {
      from: this.x,
      to: x,
      duration: Math.sqrt((2 * Math.abs(x - this.x)) / friction) * 1000,
      elapsed: 0,
    };

    return new Promise((resolve) => {
      this.finishMove = resolve;
    });
  }

  public setVisible(isVisible: boolean): void {
    this.element.classList.toggle("is-visible", isVisible);
  }

  // The frame currently shown, as a single-frame clip.
  public getClip(): SpriteClip | null {
    return this.clip
      ? {
          ...this.clip,
          x: this.clip.x + this.frameIndex * this.clip.w,
          frames: 1,
        }
      : null;
  }

  public setMask(image: string): void {
    const mask = `url("${image}")`;
    this.element.style.setProperty("-webkit-mask-image", mask);
    this.element.style.setProperty("mask-image", mask);
  }

  public update(elapsedMs: number): void {
    this.advanceFrame(elapsedMs);
    this.advanceMove(elapsedMs);
    this.render();
  }

  private startClip(clip: SpriteClip, loop: boolean): void {
    const finish = this.finishPlay;
    this.finishPlay = null;
    finish?.();

    this.clip = clip;
    this.loop = loop;
    this.frameIndex = 0;
    this.frameElapsed = 0;
  }

  private advanceFrame(elapsedMs: number): void {
    const clip = this.clip;
    if (!clip || clip.frameMs <= 0) {
      return;
    }

    this.frameElapsed += elapsedMs;
    while (this.frameElapsed >= clip.frameMs) {
      this.frameElapsed -= clip.frameMs;
      if (this.frameIndex < clip.frames - 1) {
        this.frameIndex += 1;
      } else if (this.loop) {
        this.frameIndex = 0;
      } else {
        const finish = this.finishPlay;
        this.finishPlay = null;
        finish?.();
        return;
      }
    }
  }

  private advanceMove(elapsedMs: number): void {
    if (this.slide) {
      this.advanceSlide(elapsedMs);
      return;
    }

    if (!this.target) {
      return;
    }

    const distance = this.target.x - this.x;
    const step = (this.target.speed * elapsedMs) / 1000;
    if (Math.abs(distance) > step) {
      this.x += Math.sign(distance) * step;
      return;
    }

    this.x = this.target.x;
    this.target = null;
    const finish = this.finishMove;
    this.finishMove = null;
    finish?.();
  }

  private advanceSlide(elapsedMs: number): void {
    const slide = this.slide;
    if (!slide) {
      return;
    }

    slide.elapsed += elapsedMs;
    if (slide.elapsed < slide.duration) {
      const rest = 1 - slide.elapsed / slide.duration;
      this.x = slide.from + (slide.to - slide.from) * (1 - rest * rest);
      return;
    }

    this.x = slide.to;
    this.slide = null;
    const finish = this.finishMove;
    this.finishMove = null;
    finish?.();
  }

  private render(): void {
    const clip = this.clip;
    if (!clip) {
      return;
    }

    const width = clip.w * this.scale;
    const style = this.element.style;
    style.width = `${width}px`;
    style.height = `${clip.h * this.scale}px`;
    style.backgroundPosition = `-${
      (clip.x + this.frameIndex * clip.w) * this.scale
    }px -${clip.y * this.scale}px`;
    style.transform = `translate3d(${Math.round(this.x - width / 2)}px, ${-Math.round(
      this.bottom + this.lift,
    )}px, 0) scaleX(${this.facing})`;
    style.zIndex = getPetDepth(this.depthBottom ?? this.bottom);
  }
}

class CatCompanion {
  private readonly sprite: SheetSprite;
  private lastActivity = "";
  private destination: number | null = null;
  private hasEntered = false;

  public constructor(
    variant: CatCompanionVariantConfig,
    private readonly addon: CatCompanionAddon,
  ) {
    this.sprite = addon.createCatSprite(variant);
    this.sprite.element.setAttribute("role", "img");
    this.sprite.element.setAttribute("aria-label", variant.label ?? variant.id);
  }

  public isPresent(): boolean {
    return this.hasEntered;
  }

  public getRestingX(): number {
    return this.destination ?? this.sprite.x;
  }

  public async live(spawnDelay: number): Promise<void> {
    await wait(spawnDelay);
    await this.enter();

    for (;;) {
      await this.playNextActivity();
    }
  }

  private async enter(): Promise<void> {
    const fromLeft = Math.random() < 0.5;
    const offscreen = this.addon.getCatClip("Run").w * this.addon.scale;

    this.sprite.bottom = this.addon.pickBottom();
    this.sprite.x = fromLeft ? -offscreen : window.innerWidth + offscreen;
    this.sprite.play(this.addon.getCatClip("Run"));
    this.sprite.setVisible(true);
    this.hasEntered = true;

    await wait(randomBetween(500, 2_000));
    await this.runTo(this.addon.pickSpot(this));
  }

  private async playNextActivity(): Promise<void> {
    if (
      this.addon.measureGap(this, this.sprite.x) < this.addon.getMinDistance()
    ) {
      await this.runTo(this.addon.pickSpot(this));
    }

    const toys = this.addon.getToys();
    if (toys.length > 0 && Math.random() < this.addon.getToyChance()) {
      await this.playWithToy(
        pickWeighted(toys.map((toy) => [toy, toyWeights[toy]])),
      );
      this.lastActivity = "toy";
      return;
    }

    const activity = pickWeighted(
      restActivities.filter(([name]) => name !== this.lastActivity),
    );
    this.lastActivity = activity;

    switch (activity) {
      case "idle":
        await this.repeat("Idle", randomBetween(5, 10));
        break;
      case "groom":
        await this.repeat("Idle2", randomBetween(2, 4));
        break;
      case "sit":
        await this.hold("Sitting", randomBetween(6_000, 14_000));
        break;
      case "nap":
        await this.hold("Sleep", randomBetween(15_000, 35_000));
        break;
      case "wander":
        await this.runTo(this.addon.pickSpot(this));
        break;
      default:
        await this.repeat("Jump", 1);
    }
  }

  private playWithToy(toy: CatToyName): Promise<void> {
    switch (toy) {
      case "ball":
        return this.playWithBall();
      case "mouse":
        return this.chaseMouse();
      case "wand":
        return this.playWithWand();
      default:
        return this.eatFish();
    }
  }

  private async playWithBall(): Promise<void> {
    const scale = this.addon.scale;
    const ball = this.addon.createToySprite(
      pickRandom(ballSprites),
      this.sprite,
    );
    const reach = 14 * scale;

    ball.bottom = this.sprite.bottom + catFootGapPx * scale;
    ball.x = this.pickNearbyX(140, 360);
    ball.setVisible(true);

    for (let pounce = randomBetween(2, 4); pounce > 0; pounce -= 1) {
      await this.runTo(ball.x + (ball.x > this.sprite.x ? -reach : reach));
      this.face(ball.x);

      const kick = ball.slideTo(this.pickNearbyX(140, 320), ballFriction);
      await this.repeat("Jump", 1);
      await kick;
    }

    await this.repeat("Idle", 1);
    await this.removeToy(ball);
  }

  private async chaseMouse(): Promise<void> {
    const mouse = this.addon.createToySprite("mouse", this.sprite);
    const mouseClip = this.addon.getToyClip("mouse");
    const offscreen = mouseClip.w * this.addon.scale;
    const [minX, maxX] = this.addon.getBounds();
    // The mouse comes from the far side, so there is room to chase it back.
    const fromLeft = this.sprite.x - minX > maxX - this.sprite.x;
    const edgeX = fromLeft ? -offscreen : window.innerWidth + offscreen;

    mouse.bottom = this.sprite.bottom;
    mouse.x = edgeX;
    mouse.setVisible(true);

    this.sprite.play(this.addon.getCatClip("Sitting"));
    await mouse.moveTo(
      this.sprite.x + (fromLeft ? -1 : 1) * mouseNoticeDistancePx,
      this.addon.runSpeed * 0.7,
    );

    // Both freeze for a beat when the cat spots it.
    const frozenFrame = mouse.getClip();
    if (frozenFrame) {
      mouse.play(frozenFrame);
    }
    this.face(mouse.x);
    await this.startle();

    mouse.play(mouseClip);
    const escape = mouse.moveTo(edgeX, this.addon.runSpeed * 1.2);
    await this.runTo(fromLeft ? minX : maxX);
    await escape;
    this.addon.removeSprite(mouse);
    await this.repeat("Idle", 1);
  }

  private async playWithWand(): Promise<void> {
    const scale = this.addon.scale;
    const wand = this.addon.createToySprite("wand", this.sprite);

    this.face(window.innerWidth / 2);
    wand.facing = this.sprite.facing;
    wand.x = this.sprite.x + this.sprite.facing * 26 * scale;
    wand.bottom = this.sprite.bottom + 12 * scale;
    wand.setVisible(true);

    await this.hold("Sitting", 1_200);
    for (let jump = randomBetween(2, 4); jump > 0; jump -= 1) {
      await this.repeat("Jump", 1);
      await this.hold("Idle", randomBetween(300, 900));
    }

    await this.removeToy(wand);
  }

  private async eatFish(): Promise<void> {
    const scale = this.addon.scale;
    const fishCount = this.addon.getToyClip("fish").frames;
    const fish = this.addon.createToySprite(
      "fish",
      this.sprite,
      randomBetween(0, fishCount - 1),
    );

    fish.bottom = this.sprite.bottom + catFootGapPx * scale;
    fish.x = this.pickNearbyX(140, 360);
    fish.setVisible(true);

    const reach = 16 * scale;
    await this.runTo(fish.x + (fish.x > this.sprite.x ? -reach : reach));
    this.face(fish.x);
    await this.hold("Sitting", 800);
    await this.addon.eatToy(fish, 3_200);
    await wait(500);
    this.addon.removeSprite(fish);
  }

  // A "!" over the head and a small hop, to show the cat noticed something.
  private async startle(): Promise<void> {
    const scale = this.addon.scale;

    showPetAlert(
      this.sprite.x + this.sprite.facing * 5 * scale,
      this.sprite.bottom + 29 * scale,
      this.sprite.bottom,
      scale,
    );
    this.sprite.play(this.addon.getCatClip("Idle"));
    this.sprite.lift = 3 * scale;
    await wait(140);
    this.sprite.lift = 0;
    await wait(610);
  }

  private async removeToy(toy: SheetSprite): Promise<void> {
    toy.setVisible(false);
    await wait(400);
    this.addon.removeSprite(toy);
  }

  private async runTo(x: number): Promise<void> {
    this.destination = x;
    this.sprite.play(this.addon.getCatClip("Run"));
    await this.sprite.moveTo(x, this.addon.runSpeed);
    this.destination = null;
    this.sprite.play(this.addon.getCatClip("Idle"));
  }

  private async repeat(animation: string, times: number): Promise<void> {
    for (let index = 0; index < times; index += 1) {
      await this.sprite.playOnce(this.addon.getCatClip(animation));
    }
  }

  private async hold(animation: string, duration: number): Promise<void> {
    this.sprite.play(this.addon.getCatClip(animation));
    await wait(duration);
  }

  private face(x: number): void {
    if (x !== this.sprite.x) {
      this.sprite.facing = x > this.sprite.x ? 1 : -1;
    }
  }

  private pickNearbyX(minDistance: number, maxDistance: number): number {
    const [minX, maxX] = this.addon.getBounds();
    const distance = randomBetween(minDistance, maxDistance);
    const candidates = [this.sprite.x - distance, this.sprite.x + distance].filter(
      (x) => x >= minX && x <= maxX,
    );

    if (candidates.length === 0) {
      return randomBetween(minX, maxX);
    }

    // Prefer the side that leads away from the other cats.
    const gaps = candidates.map((x) => this.addon.measureGap(this, x));
    return gaps.every((gap) => gap === gaps[0])
      ? pickRandom(candidates)
      : candidates[gaps.indexOf(Math.max(...gaps))];
  }
}

async function fetchJson<T>(url: string): Promise<T> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Unable to load cat companion asset ${url}: ${response.status}`);
  }

  return (await response.json()) as T;
}

function pickRandom<T>(items: T[]): T {
  return items[randomBetween(0, items.length - 1)];
}

function pickWeighted<T>(items: Array<[T, number]>): T {
  let roll = Math.random() * items.reduce((total, [, weight]) => total + weight, 0);
  for (const [item, weight] of items) {
    roll -= weight;
    if (roll < 0) {
      return item;
    }
  }

  return items[items.length - 1][0];
}

function randomBetween(min: number, max: number): number {
  if (max <= min) {
    return Math.round(min);
  }

  return Math.round(min + Math.random() * (max - min));
}

function wait(duration: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, duration));
}
